<app-docs-shell
  [flatGroups]="flatGroups"
  [panels]="panels"
  [activePanel]="activePanel()"
  (navigate)="onNavigate($event)"
  basePath="/docs"
  heading="Clinical AI Studio"
  subheading="User guide &mdash; USDM Builder &amp; SDTM Builder"
>
  <!-- =================================================================
       INTRODUCTION
       ================================================================= -->

  <!-- ========================== OVERVIEW ========================== -->
  <section id="overview" class="scroll-mt-16">
    <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">User Guide</p>
    <h1 class="text-5xl font-bold mb-6 text-black tracking-tight">Clinical AI Studio</h1>
    <p class="text-lg text-black leading-relaxed font-light mb-8">
      <b>Clinical AI Studio</b> is an AI-assisted platform that turns clinical study documents into
      submission-ready CDISC deliverables. This guide takes a brand-new user from creating an account
      through to downloading finished outputs &mdash; one tool at a time, with you in control at every step.
    </p>

    <h2 class="text-2xl font-semibold mb-4 text-black">The two tools</h2>
    <div class="grid gap-5 md:grid-cols-2 mb-10">
      <div class="rounded-2xl border border-gray-200 p-6">
        <div class="flex items-center gap-3 mb-3">
          <span class="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#1594fd] text-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13l2 2 4-4"/></svg>
          </span>
          <h3 class="text-xl font-semibold text-black">USDM Builder</h3>
        </div>
        <p class="text-sm text-gray-700 leading-relaxed">Turns a study protocol into a validated <b>Unified Study Data Model (USDM)</b>. See the <b>USDM Builder</b> guide.</p>
      </div>
      <div class="rounded-2xl border border-gray-200 p-6">
        <div class="flex items-center gap-3 mb-3">
          <span class="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#1594fd] text-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>
          </span>
          <h3 class="text-xl font-semibold text-black">SDTM Builder</h3>
        </div>
        <p class="text-sm text-gray-700 leading-relaxed">Converts raw study data into the <b>SDTM</b> standard &mdash; annotated CRF, mapping spec, R code and datasets. See the <b>SDTM Builder</b> guide.</p>
      </div>
    </div>

    <h2 class="text-2xl font-semibold mb-4 text-black">How the tools relate</h2>
    <p class="text-black leading-relaxed font-light mb-10">
      The USDM Builder and the SDTM Builder are two <b>separate, independent tools</b> &mdash; use either on
      its own. They do connect at one optional point: the validated USDM you build with the USDM Builder can
      be reused in the SDTM Builder to generate the SDTM <b>Trial Design</b> datasets.
    </p>

    <h2 class="text-2xl font-semibold mb-4 text-black">How this guide is organized</h2>
    <div class="rounded-2xl border border-gray-200 p-6 bg-white mb-8">
      <ol class="space-y-4">
        <li class="flex gap-4">
          <span class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-50 text-[#1594fd] text-sm font-bold flex items-center justify-center">1</span>
          <div><b class="text-black">Getting started</b><p class="text-sm text-gray-700 leading-relaxed">Create your account, sign in, unlock access and connect a repository. Shared by both tools &mdash; do this once.</p></div>
        </li>
        <li class="flex gap-4">
          <span class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-50 text-[#1594fd] text-sm font-bold flex items-center justify-center">2</span>
          <div><b class="text-black">User Guide</b><p class="text-sm text-gray-700 leading-relaxed">Build, validate and view a USDM, then run the SDTM pipeline through to downloadable deliverables.</p></div>
        </li>
        <li class="flex gap-4">
          <span class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-50 text-[#1594fd] text-sm font-bold flex items-center justify-center">3</span>
          <div><b class="text-black">User Architecture</b><p class="text-sm text-gray-700 leading-relaxed">How the platform is put together &mdash; the systems behind the tools.</p></div>
        </li>
      </ol>
    </div>

    <div class="rounded-xl border-l-4 border-[#1594fd] bg-blue-50 px-5 py-4">
      <p class="text-sm text-black leading-relaxed">
        <b>Before you begin.</b> You'll need a modern web browser and your study documents &mdash; a
        <b>protocol PDF</b> for the USDM Builder, and the <b>ALS</b>, <b>blank CRF</b> and <b>raw datasets</b>
        for the SDTM Builder. Each section lists exactly what it needs when you get there.
      </p>
    </div>
  </section>

  <!-- =================================================================
       GETTING STARTED  (shared by both tools)
       ================================================================= -->

  <!-- ======================= CREATE ACCOUNT ======================= -->
  <section id="create-account" class="scroll-mt-16">
    <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Create your account</h1>
    <p class="text-black leading-relaxed font-light mb-6">
      New to Clinical AI Studio? On the sign-in screen choose <b>Sign up</b> to open the account form.
    </p>
    <img src="assets/quickstart2/signup.png" alt="Create your account form"
         class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
    <p class="text-black leading-relaxed font-light mb-6">
      Enter your <b>full name</b>, <b>email</b> and a <b>password</b> (at least 8 characters), confirm it,
      then click <b>Sign up</b>.
    </p>
    <img src="assets/quickstart2/signup-filled.png" alt="Sign-up form filled in"
         class="rounded-lg shadow-lg border border-gray-200 w-full">
  </section>

  <!-- ========================== SIGN IN =========================== -->
  <section id="sign-in" class="scroll-mt-16">
    <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Sign in</h1>
    <p class="text-black leading-relaxed font-light mb-6">
      Sign in with the email and password you registered, then click <b>Sign in</b>.
    </p>
    <img src="assets/quickstart2/login.png" alt="Sign in"
         class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
    <div class="rounded-xl border-l-4 border-[#1594fd] bg-blue-50 px-5 py-4">
      <p class="text-sm text-black leading-relaxed">
        The first time you sign in you'll land on the <b>Access Required</b> screen, not the home page &mdash;
        Clinical AI Studio is in closed beta. Redeem an access code (next) to continue.
      </p>
    </div>
  </section>

  <!-- ========================= ACCESS CODE ======================== -->
  <section id="access" class="scroll-mt-16">
    <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Access code</h1>
    <p class="text-black leading-relaxed font-light mb-6">
      Clinical AI Studio is currently in <b>closed beta</b>. Right after you sign in, the
      <b>Access Required</b> screen appears &mdash; you need a valid access code to unlock the platform.
    </p>
    <img src="assets/quickstart2/access-code.png" alt="Access Required — redeem your access code"
         class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
    <p class="text-black leading-relaxed font-light mb-6">
      Type your code into the <b>Enter Access Code</b> field and click <b>Redeem Code</b>. Once accepted,
      you land on the <b>Clinical AI Studio</b> home page &mdash; your starting point for every tool.
    </p>
    <img src="assets/quickstart2/home.png" alt="Clinical AI Studio home page"
         class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
    <div class="rounded-xl border-l-4 border-amber-400 bg-amber-50 px-5 py-4">
      <p class="text-sm text-black leading-relaxed">
        No access code yet? Request one by emailing
        <span class="font-mono text-xs bg-white px-1.5 py-0.5 rounded border">info&#64;ailens.ai</span>.
      </p>
    </div>
  </section>

  <!-- ====================== CONNECT REPOSITORY ==================== -->
  <section id="repository" class="scroll-mt-16">
    <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Connect a repository</h1>
    <p class="text-black leading-relaxed font-light mb-6">
      Before you can explore data, chat with the AI or build a USDM, you need a <b>repository</b> &mdash; a
      cloud folder that holds a study's files and results. Every task runs against a connected repository,
      so this is the first thing to do after signing in.
    </p>
    <p class="text-black leading-relaxed font-light mb-6">
      On the home page, click <b>Select Repository</b> in the chat box. In the <b>Connect to Repository</b>
      dialog, choose <b>Cloud Storage</b>.
    </p>
    <img src="assets/quickstart2/connect-repository.png" alt="Connect to Repository — choose storage method"
         class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">
    <p class="text-black leading-relaxed font-light mb-6">
      On the <b>Create Repository</b> tab, enter a name and click <b>Create Repository</b>. Already have one?
      Switch to <b>Existing Repositories</b>, pick it from the list and click <b>Connect</b>. On success,
      click <b>Start Working</b> &mdash; the repository's name then shows in the chat box.
    </p>
    <img src="assets/quickstart2/create-repository.png" alt="Create Repository tab with name field"
         class="rounded-lg shadow-lg border border-gray-200 w-full">
  </section>

  <!-- ====================== WORKSPACE & CHAT ===================== -->
  <section id="workspace" class="scroll-mt-16">
    <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Workspace &amp; AI chat</h1>
    <p class="text-black leading-relaxed font-light mb-6">
      With a repository connected, the home chat box becomes your AI assistant for clinical data. Type a
      question (or pick a sample prompt) and press send &mdash; you're taken into the <b>Workspace</b>, a
      three-pane layout you drive entirely from chat.
    </p>
    <img src="assets/quickstart2/workspace.png" alt="Workspace — chat, files, editor and terminal"
         class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">
    <div class="grid gap-4 md:grid-cols-2">
      <div class="rounded-xl border border-gray-200 p-5">
        <h3 class="font-semibold text-black mb-2">Chat &mdash; left pane</h3>
        <p class="text-sm text-gray-700 leading-relaxed">Ask questions in plain language. The assistant and its agents work through your repository's data to answer, with a typing indicator while they run. You can edit an earlier prompt or rate an answer.</p>
      </div>
      <div class="rounded-xl border border-gray-200 p-5">
        <h3 class="font-semibold text-black mb-2">Files &mdash; top right</h3>
        <p class="text-sm text-gray-700 leading-relaxed">The repository's file tree. Anything the agent reads or generates &mdash; R scripts, datasets, tables and outputs &mdash; shows up here. Click a file to open it.</p>
      </div>
      <div class="rounded-xl border border-gray-200 p-5">
        <h3 class="font-semibold text-black mb-2">Editor / Viewer &mdash; centre</h3>
        <p class="text-sm text-gray-700 leading-relaxed">Opens the selected file so you can view or edit R code and inspect generated results, right alongside the conversation.</p>
      </div>
      <div class="rounded-xl border border-gray-200 p-5">
        <h3 class="font-semibold text-black mb-2">Terminal &mdash; bottom right</h3>
        <p class="text-sm text-gray-700 leading-relaxed">An integrated shell for running commands and analyses directly against the workspace.</p>
      </div>
    </div>
  </section>

  <!-- =================================================================
       CHOOSE YOUR PATH  (reveals a sub-guide below, on this same page)
       ================================================================= -->
  <section id="choose-path" class="scroll-mt-16">
    <div class="border-t border-gray-200 pt-10 mb-8"></div>
    <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Where to next</p>
    <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Guide &amp; Architecture</h1>
    <p class="text-black leading-relaxed font-light mb-8">
      You're set up. Pick what you need and it opens right below. The <b>User Guide</b> walks you through
      using each tool step by step; <b>User Architecture</b> explains how the platform is built underneath.
    </p>

    <div class="grid gap-5 md:grid-cols-2">
      <button type="button" (click)="select('guide')"
         class="text-left block p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg"
         [class]="view() === 'guide' ? 'border-[#1594fd] ring-2 ring-[#1594fd]/25 shadow-lg' : 'border-gray-200 hover:border-[#1594fd]'">
        <div class="flex items-center gap-3 mb-3">
          <span class="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#1594fd] text-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          </span>
          <h3 class="text-xl font-semibold text-black">User Guide</h3>
        </div>
        <p class="text-sm text-gray-700 leading-relaxed mb-3">How to use the platform &mdash; choose a tool, build a validated USDM, and run the SDTM pipeline from upload through to downloadable deliverables. Step-by-step, with screenshots.</p>
        <span class="text-sm text-[#1594fd] font-semibold">{{ view() === 'guide' ? 'Showing below ↓' : 'Open the User Guide →' }}</span>
      </button>
      <button type="button" (click)="select('architecture')"
         class="text-left block p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg"
         [class]="view() === 'architecture' ? 'border-[#1594fd] ring-2 ring-[#1594fd]/25 shadow-lg' : 'border-gray-200 hover:border-[#1594fd]'">
        <div class="flex items-center gap-3 mb-3">
          <span class="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#1594fd] text-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/></svg>
          </span>
          <h3 class="text-xl font-semibold text-black">User Architecture</h3>
        </div>
        <p class="text-sm text-gray-700 leading-relaxed mb-3">How the platform is put together &mdash; the systems, data flow and components behind the tools. Reference material for a deeper understanding of what happens under the hood.</p>
        <span class="text-sm text-[#1594fd] font-semibold">{{ view() === 'architecture' ? 'Showing below ↓' : 'Open User Architecture →' }}</span>
      </button>
    </div>
  </section>

  <!-- =================================================================
       SELECTED SUB-GUIDE  (rendered on the same page, below the chooser)
       ================================================================= -->
  @if (view() === 'guide') {
    <app-docs-guide [tool]="guideTool()" (toolSelected)="selectTool($event)" />
  } @else if (view() === 'architecture') {
    <app-docs-architecture />
  }

  <!-- ===================== VIDEO GUIDE (always at page end) ===================== -->
  <section class="scroll-mt-16">
    <div class="border-t border-gray-200 pt-10 mb-6"></div>
    <h2 class="text-2xl font-semibold mb-3 text-black">Video guide</h2>
    <p class="text-black leading-relaxed font-light mb-5">
      A short walkthrough of Clinical AI Studio &mdash; watch it alongside whichever section you have open above.
    </p>
    <div class="relative w-full h-[55vh] overflow-hidden max-lg:h-auto rounded-2xl border border-gray-200 shadow-lg">
      <iframe
        class="w-full h-full object-cover block max-lg:object-contain"
        src="https://www.youtube.com/embed/gKTqC6t_wc8?autoplay=1&mute=1&loop=1&playlist=gKTqC6t_wc8"
        title="Clinical AI Studio — video guide"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      ></iframe>
    </div>
  </section>

  <a href="/docs#overview" title="Back to top" aria-label="Back to top"
     class="fixed right-6 bottom-6 z-50 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#1594fd] text-white shadow-lg hover:-translate-y-1 transition-transform duration-200">
    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
      <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7"/>
    </svg>
  </a>
</app-docs-shell>
